import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { loginSuccess } from '../redux/authSlice';
import { signInWithGoogle, logOut, onAuthStateChange } from '../config/firebase';
import { API_BASE_URL } from '../config';
import { trackEvent } from '../services/analytics';
import axios from 'axios';

export const useFirebaseAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        try {
          // Get Firebase ID token
          const idToken = await firebaseUser.getIdToken();
          
          // Send token to backend for verification
          const response = await axios.post(`${API_BASE_URL}/api/auth/firebase`, {
            idToken,
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0]
          });

          const { token, email } = response.data;
          
          // Store tokens
          localStorage.setItem('token', token);
          localStorage.setItem('email', email);
          
          // Update Redux state
          dispatch(loginSuccess({ token, email }));
          
          // Track successful login
          trackEvent('login_success', JSON.stringify({
            method: 'firebase',
            email: email ? email.substring(0, 3) + '***' : 'anonymous',
            timestamp: new Date().toISOString(),
          }));

          setUser(firebaseUser);
          
          // Small delay to ensure Redux state is updated before any navigation
          setTimeout(() => {
            // Check for pending checkout and handle navigation
            const checkoutData = localStorage.getItem('pendingCheckout') || 
                                 localStorage.getItem('pendingCheckout_backup') || 
                                 localStorage.getItem('authflow_pricing');
            
            if (checkoutData) {
              try {
                const { plan, billing } = JSON.parse(checkoutData);
                localStorage.removeItem('pendingCheckout');
                localStorage.removeItem('pendingCheckout_backup');
                localStorage.removeItem('authflow_pricing');
                navigate(`/checkout?plan=${plan}&billing=${billing}`);
              } catch (e) {
                navigate('/dashboard');
              }
            } else {
              navigate('/dashboard');
            }
          }, 100); // Small delay to ensure Redux sync
          
        } catch (error: any) {
          console.error('Firebase auth error:', error);
          setError(error.response?.data?.error || 'Authentication failed');
          
          trackEvent('login_failed', JSON.stringify({
            method: 'firebase',
            error: error.message,
            timestamp: new Date().toISOString(),
          }));
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    // Popup mode doesn't need redirect result handling

    return () => unsubscribe();
  }, [dispatch, navigate]);

  const signIn = async () => {
    try {
      setError(null);
      setLoading(true);
      await signInWithGoogle();
    } catch (error: any) {
      console.error('Sign in error:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const signOutUser = async () => {
    try {
      await logOut();
      localStorage.removeItem('token');
      localStorage.removeItem('email');
      navigate('/auth');
    } catch (error: any) {
      console.error('Sign out error:', error);
    }
  };

  return {
    user,
    loading,
    error,
    signIn,
    signOut: signOutUser
  };
};